
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatButton } from "@angular/material/button";
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipRemove, MatChipRow, MatChipSet } from "@angular/material/chips";
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { MatTooltip } from "@angular/material/tooltip";
import { Subject, takeUntil } from 'rxjs';
import { DeleteModalComponent } from "../../../shared/delete-modal/delete-modal.component";

type SanitizerKind = 'name';

@Component({
  selector: 'app-crawling-web-modal',
  imports: [
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatSelectModule,
    CommonModule,
    MatNativeDateModule,
    MatCheckboxModule,
    MatSliderModule,
    MatButton,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltip,
    DeleteModalComponent,
    MatChipSet,
    MatChipRow,
    MatChipRemove
],
  templateUrl: './crawling-web-modal.component.html',
  styleUrl: './crawling-web-modal.component.scss',
  providers: [
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } }
  ]
})
export class CrawlingWebModalComponent implements OnInit, OnDestroy {
  action: 'add' | 'edit' | 'delete' | undefined;
  formData!: FormGroup;
  nameTable: 'crawling-web-urls-detail' | 'crawling-web-schedules-detail' | 'crawling-web' | '' | undefined;

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  selectedFile: File | null = null;

  // Opzioni select "minute"
  minuteSelectOptions = Array.from({length: 60}, (_ , i)=>({
    value: i.toString(),
    label: i.toString()
  }));
  // Opzioni select "hour"
  hourSelectOptions = Array.from({length: 24}, (_ , i)=>({
    value: i.toString(),
    label: i.toString()
  }));

  // Opzioni select "day"
  weekDays = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
  daySelectOptions = this.weekDays.map(day => ({value: day, label: day}));
  
  isLoading = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly dialogRef: MatDialogRef<CrawlingWebModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      action: 'add' | 'edit' | 'delete',
      payload: any,
      selectOptions: {
        selectMinute: any[],
        selectHour: any[],
        selectDay: any[]
      },
      nameTable: 'crawling-web-urls-detail' | 'crawling-web-schedules-detail' | 'crawling-web' | ''
    },
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.action = this.data.action;
    this.nameTable = this.data.nameTable;
    console.log('dettaglio tabella', this.nameTable);
    this.formData = this.fb.group({
      crawlId: ['', Validators.required],
      name: [
        '', [Validators.required, Validators.pattern(/^[a-zA-Z\d_-]{1,500}$/)]
      ],
      selectDay: [null, Validators.required],
      selectHour: [null, Validators.required],
      selectMinute: [null, Validators.required],
      sitemap: [false],
      confId: [''], //hidden
      extraInputUrlsArray: this.fb.array([
        this.createUrlGroup()
      ])
    })

    if (this.nameTable === 'crawling-web-urls-detail') {
      this.formData.get('crawlId')?.clearValidators();
      this.formData.get('name')?.clearValidators();
      this.formData.get('selectDay')?.clearValidators();
      this.formData.get('selectHour')?.clearValidators();
      this.formData.get('selectMinute')?.clearValidators();
    } else if (this.nameTable === 'crawling-web-schedules-detail') {
      this.formData.get('crawlId')?.clearValidators();
      this.formData.get('name')?.clearValidators();
      (this.formData.get('extraInputUrlsArray') as FormArray).controls.forEach(control => {
        control.get('url')?.clearValidators();
        control.get('url')?.updateValueAndValidity();
      });
    } else if(this.nameTable === 'crawling-web'){
      this.formData.get('selectDay')?.clearValidators();
      this.formData.get('selectHour')?.clearValidators();
      this.formData.get('selectMinute')?.clearValidators();
    }

    Object.keys(this.formData.controls).forEach(key => {
      this.formData.get(key)?.updateValueAndValidity();
    });

    //PATCH EDIT
    if(this.data.payload) {
      this.formData.patchValue(this.data.payload, {emitEvent: false});
      //popolamento manuale per l'edit dell'url nella tabella di dettaglio
      if(this.action === 'edit' && this.nameTable === 'crawling-web-urls-detail'){
        const toModifyUrl = this.data.payload.url;
        if(toModifyUrl){
          this.extraUrls.at(0).patchValue({url: toModifyUrl}, {emitEvent: false});
        }
      }
    }

    //resto in ascolto dei cambiamenti del campo sitemap
    this.formData.get('sitemap')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      setTimeout(()=> {
        //pulizia input url
        this.clearExtraUrls();
        this.addExtraUrl();
      });
    });

    this.cdr.detectChanges();
  }

  createUrlGroup(){
    return this.fb.group({
      url: ['', Validators.required]
    })
  }

  get extraUrls(): FormArray {
    return this.formData.get('extraInputUrlsArray') as FormArray;
  }

  addExtraUrl() {
    this.extraUrls.push(this.createUrlGroup());
  }

  removeExtraUrl(index: number) {
    this.extraUrls.removeAt(index);
  }

  setExtraUrl(urls: any[]) {
    const controls = urls.map(item => this.fb.group({
      url: [item.url ?? '', Validators.required]
    }));
    this.formData.setControl('extraInputUrlsArray', this.fb.array(controls));

    this.isLoading = false;
  }

  clearExtraUrls(){
    this.extraUrls.clear();
  }

  isMassiveImport(){
    this.clearExtraUrls();
    this.addExtraUrl();
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event){
    const input = event.target as HTMLInputElement;
    if(input.files && input.files.length > 0){
      this.selectedFile = input.files[0];
      this.updateUrlValidations(false);
    }
  }

  removeImportedFile(){
    this.selectedFile = null;
    
    if(this.fileInput?.nativeElement){
      this.fileInput.nativeElement.value = '';
    }

    this.updateUrlValidations(true);
  }

  private updateUrlValidations(isRequired: boolean){
    this.extraUrls.controls.forEach(control => {
      const urlControl = control.get('url');
      console.log('url control', urlControl);
      if(urlControl){
        if(isRequired){
          urlControl.setValidators([Validators.required]);
        } else {
          urlControl.clearValidators();
        }
        urlControl.updateValueAndValidity();
      }
    })
  }

  get title(): string {
      switch (this.action) {
        case 'add': return 'Aggiungi';
        case 'edit': return 'Modifica';
        case 'delete': return 'Elimina';
        default: return '';
      }
  }

  onModalClose(): void {
    this.dialogRef.close();
  }

  onModalSave(): void {
    if (this.formData.invalid && this.action !== 'delete') {
      console.log('invalido');
      return;
    }
    let dataToPass: any;

    if (this.action === 'delete') {
      dataToPass = this.data.payload;
    } else if (this.action === 'edit') {
      if (this.data.payload.id) {
        dataToPass = { ...this.formData.value, id: this.data.payload.id };
      } else {
        dataToPass = { ...this.formData.value };
      }

    } else if (this.action === 'add') {
      dataToPass = this.formData.value;
    }

    this.dialogRef.close({
      payload: dataToPass,
      document: this.selectedFile || null
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

